import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Play } from "lucide-react";
import { scrollToId } from "./SmoothScroll";

const EASE = [0.16, 1, 0.3, 1];
const LINES = [
  { text: "Money", cls: "" },
  { text: "Alwayz", cls: "outline-text" },
  { text: "Entertainment", cls: "text-[var(--cash)] text-[0.52em]" },
];

const MaskLine = ({ children, i, cls }) => (
  <span className="block overflow-hidden pb-[0.04em]">
    <motion.span className={`block ${cls}`} initial={{ y: "110%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const Bill = ({ className, delay, rotate }) => (
  <motion.div className={`pointer-events-none absolute h-10 w-24 rounded-sm border border-[rgba(182,242,74,0.4)] bg-[rgba(182,242,74,0.08)] backdrop-blur-sm ${className}`}
    initial={{ opacity: 0 }} animate={{ opacity: 1, y: [0, -14, 0], rotate: [rotate, rotate + 8, rotate] }}
    transition={{ opacity: { delay: 1.4 }, y: { duration: 6, repeat: Infinity, delay }, rotate: { duration: 6, repeat: Infinity, delay } }}>
    <span className="flex h-full items-center justify-center font-display text-lg text-[var(--cash)]">$100</span>
  </motion.div>
);

const LogoMedallion = () => {
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div className="relative mx-auto aspect-square w-[78vw] max-w-[440px] [perspective:1000px]" onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }} data-testid="hero-logo-medallion">
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative h-full w-full [transform-style:preserve-3d]"
        initial={{ scale: 0.6, opacity: 0, rotate: -40 }} animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ duration: 1.6, delay: 0.5, ease: EASE }}>
        <div className="absolute inset-[-6%] rounded-full border border-dashed border-[rgba(182,242,74,0.35)] animate-spin-slow" />
        <div className="absolute inset-0 rounded-full bg-[var(--cash)] opacity-20 blur-[80px]" />
        <img src="/mae-logo-ink.webp" alt="Money Alwayz Entertainment — MAE logo"
          className="relative h-full w-full rounded-full object-cover shadow-[0_40px_120px_rgba(0,0,0,0.8)] [transform:translateZ(40px)]" />
      </motion.div>
      <Bill className="-left-4 top-10 sm:-left-10" delay={0} rotate={-18} />
      <Bill className="-right-2 bottom-16 sm:-right-8" delay={1.5} rotate={14} />
    </div>
  );
};

export const Hero = ({ latestTitle = "Paper Crown" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, -80]);
  return (
    <section id="top" ref={ref} data-testid="hero-section" className="guilloche relative min-h-screen overflow-hidden px-4 pb-16 pt-28 sm:px-8 lg:px-16">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:min-h-[85vh] lg:grid-cols-12">
        <motion.div style={{ y: yText }} className="relative z-10 lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">
            Independent record label <span className="text-[var(--cash)]">/</span> Est. MAE
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-[17vw] uppercase leading-[0.86] sm:text-[13vw] lg:text-[8.6rem]">
            {LINES.map((l, i) => <MaskLine key={l.text} i={i} cls={l.cls}>{l.text}</MaskLine>)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-md text-base text-zinc-300 sm:text-lg">
            Music and videos from the <span className="font-serif-i text-2xl text-[var(--bone)]">Money Alwayz</span> camp.
            Press play, then go stream everywhere.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-watch-btn" onClick={() => scrollToId("videos")}
              className="group flex items-center gap-3 rounded-full bg-[var(--cash)] py-2 pl-2 pr-6 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[var(--ink)] transition-transform hover:scale-105">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--ink)] text-[var(--cash)] transition-transform group-hover:rotate-[120deg]"><Play size={16} fill="currentColor" /></span>
              Watch {latestTitle}
            </button>
            <button data-testid="hero-stream-btn" onClick={() => scrollToId("stream")}
              className="flex items-center gap-2 rounded-full border border-[var(--bone)] px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:bg-[var(--bone)] hover:text-[var(--ink)]">
              Stream hub <ArrowDownRight size={16} />
            </button>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yLogo }} className="relative lg:col-span-5">
          <LogoMedallion />
        </motion.div>
      </div>
    </section>
  );
};
